import { Alert, Box, Button, Chip, CircularProgress, FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import {
  SLA_ACTIVITIES_TEXT_KEYS,
  SLA_ACTIVITY_PREFERENCE_KEYS,
  SLA_ACTIVITY_PREFERENCE_TEXT_KEYS,
  slaActivityPreferenceValue,
  slaActivityWorkingDuration,
} from "model/slaActivitiesModel";
import { SLA_MONITOR_TEXT_KEYS } from "model/slaMonitorModel";
import { SLA_HEALTH_BREACHED } from "model/slaModel";
import React from "react";
import { useSlaActivities } from "./slaActivitiesContext";
import SlaInstanceSummaryLine from "./SlaInstanceSummaryLine";

/**
 * **Strip Attivita' SLA** (piano Client sez. 5 F3c, sez. 6 riga 88, sez. 8 riga
 * 127). Montata da `src/pages/MyActivities.tsx` (la pagina attivita' esistente,
 * sez. 3) e alimentata dal contesto della superficie: qui **non** si crea una
 * pagina nuova (decisione 2, sez. 14.2).
 *
 * Cosa rende, e perche':
 * - **banner su deadline reali** (sez. 5 F3c): la riga scelta e la sua salute
 *   vengono dal payload (`slaActivityBanner`), la scadenza mostrata e'
 *   `effective_deadline_at` nella timezone dichiarata dalla riga. Il banner
 *   **non** dice "oggi": un'istanza a rischio con scadenza futura e' resa con la
 *   data e con il residuo **lavorativo** del payload
 *   (`SLA_ACTIVITIES_BANNER_RULE`). Nessuna funzione di questo file confronta la
 *   scadenza con il giorno del browser;
 * - **dismiss per utente** (sez. 5 F3c): il comando chiude il saluto con
 *   `PATCH /sla/preferences` (`greeting_dismissed`), rotta dell'utente
 *   autenticato; la preferenza vale per (utente, organizzazione) e sopravvive al
 *   reload, non e' un flag di dispositivo;
 * - **preferenze individuali** (sez. 5 F4c, sez. 14.3): digest, escalation e
 *   transazionali sono tre interruttori **indipendenti**, letti e scritti per
 *   l'organizzazione corrente;
 * - **stati**: caricamento, errore, 403, N/D (nessuno SLA attivo), dati non
 *   aggiornati (sez. 5 F3d) e modalita' fixture dichiarata;
 * - responsive (sez. 6 riga 88): su mobile la strip mostra stato, scadenza e
 *   residuo in priorita' e il comando di chiusura resta un bottone reale con
 *   area di tocco adeguata — nessuna azione delegata a uno swipe e nessun cambio
 *   di stato che l'utente non abbia chiesto.
 */
const PREFIX = "sla-activities";

/** Testo presente nel DOM (screen reader, test) ma non a video. */
const VISUALLY_HIDDEN = {
  border: 0,
  clip: "rect(0 0 0 0)",
  height: 1,
  margin: -1,
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  whiteSpace: "nowrap",
  width: 1,
} as const;

const SlaActivitiesSurface: React.FC = () => {
  const translations = useTranslations();
  const context = useSlaActivities();

  if (!context) return null;

  const {
    state,
    banner,
    bannerVisible,
    dismissBanner,
    preferences,
    preferencesForbidden,
    setPreference,
    preferencesScope,
    locale,
    processViewFor,
  } = context;

  /**
   * Residuo o ritardo lavorativo, con la label giusta: in ritardo si mostra
   * `+N` sotto "ritardo", altrimenti il residuo. Un residuo nullo o a zero senza
   * ritardo non dice nulla ("0 secondi") e non viene reso.
   */
  const overdueSeconds = banner?.countdown.overdueSeconds ?? 0;
  const remainingSeconds = banner?.countdown.remainingSeconds ?? 0;
  const countdownDuration = banner
    ? overdueSeconds > 0
      ? slaActivityWorkingDuration(overdueSeconds, locale)
      : remainingSeconds > 0
        ? slaActivityWorkingDuration(remainingSeconds, locale)
        : null
    : null;
  const countdownText = countdownDuration
    ? `${getTranslation(
        translations,
        overdueSeconds > 0
          ? SLA_MONITOR_TEXT_KEYS.columnOverdue
          : SLA_MONITOR_TEXT_KEYS.columnRemaining,
      )}: ${overdueSeconds > 0 ? "+" : ""}${countdownDuration.value} ${getTranslation(
        translations,
        countdownDuration.unitKey,
      )}`
    : null;
  // Banner su uno SLA di attivita': lo SLA del processo resta come riga secondaria.
  const bannerProcess = banner?.activityTarget ? processViewFor(banner.idprocess) : null;
  const bannerTarget = banner
    ? [banner.processDescription, banner.activityDescription].filter(Boolean).join(" · ")
    : "";

  return (
    <Stack
      data-testid={PREFIX}
      data-stale={String(state.stale)}
      data-as-of={state.asOf ?? ""}
      data-instances={state.instances.length}
      spacing={1}
      sx={{ width: "100%", px: { xs: 1, md: 0 }, pb: { xs: 1, md: 0 } }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="subtitle2" data-testid={`${PREFIX}-title`}>
          {getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.title)}
        </Typography>
        {state.asOf && (
          <Typography variant="caption" data-testid={`${PREFIX}-as-of`}>
            {getTranslation(translations, SLA_MONITOR_TEXT_KEYS.asOf)}
            {": "}
            {state.asOf}
          </Typography>
        )}
        {banner && (
          <Chip
            size="small"
            data-testid={`${PREFIX}-timezone`}
            label={`${getTranslation(translations, SLA_MONITOR_TEXT_KEYS.timezone)}: ${banner.timezoneId}`}
          />
        )}
      </Stack>

      {state.fixtures && (
        <Alert severity="info" data-testid={`${PREFIX}-fixture-notice`}>
          {getTranslation(translations, "sla.editor.fixture_notice")}
        </Alert>
      )}

      {state.loading && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} data-testid={`${PREFIX}-loading`} />
          <Typography variant="caption">
            {getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.loading)}
          </Typography>
        </Stack>
      )}

      {state.error && (
        <Alert
          severity={state.forbidden ? "warning" : "error"}
          data-testid={state.forbidden ? `${PREFIX}-forbidden` : `${PREFIX}-error`}
        >
          {getTranslation(
            translations,
            state.forbidden
              ? SLA_ACTIVITIES_TEXT_KEYS.forbidden
              : SLA_ACTIVITIES_TEXT_KEYS.loadError,
          )}
          {": "}
          {state.error}
        </Alert>
      )}

      {/* Dati non aggiornati: lo stato non resta verde (sez. 5 F3d). */}
      {state.stale && !state.error && (
        <Alert severity="warning" data-testid={`${PREFIX}-stale`}>
          {getTranslation(translations, SLA_MONITOR_TEXT_KEYS.stale)}
        </Alert>
      )}

      {!state.loading && !state.error && state.instances.length === 0 && (
        <Alert severity="info" data-testid={`${PREFIX}-empty`}>
          {getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.empty)}
        </Alert>
      )}

      {/* Banner: deadline reali del payload, nessuna equivalenza rischio = oggi. */}
      {banner && bannerVisible && (
        <Alert
          severity={
            banner.health === SLA_HEALTH_BREACHED ? "error" : "warning"
          }
          data-testid={`${PREFIX}-banner`}
          data-health={banner.health}
          aria-label={getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.bannerTitle)}
          sx={{
            py: 0,
            alignItems: "center",
            "& .MuiAlert-message": { minWidth: 0, flex: 1, py: 0.75 },
            "& .MuiAlert-action": { pt: 0, alignItems: "center" },
          }}
          data-instance-id={banner.instanceId}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => void dismissBanner()}
              data-testid={`${PREFIX}-banner-dismiss`}
              sx={{ minHeight: 44, minWidth: 44 }}
            >
              {getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.bannerDismiss)}
            </Button>
          }
        >
          {/* Due righe compatte: (1) istanza + chip dello stato **attuale**,
              (2) scadenza, residuo/ritardo e pausa. Il titolo statico
              "SLA a rischio" non e' piu' a video perche' accanto a uno stato
              "violato" si contraddiceva: resta come nome accessibile del banner. */}
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                noWrap
                title={bannerTarget}
                data-testid={`${PREFIX}-banner-target`}
                sx={{ fontWeight: 600, minWidth: 0 }}
              >
                {bannerTarget || getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.bannerTitle)}
              </Typography>
              <Chip
                size="small"
                color={banner.health === SLA_HEALTH_BREACHED ? "error" : "warning"}
                label={getTranslation(translations, banner.healthKey)}
                data-testid={`${PREFIX}-banner-health`}
                sx={{ height: 20, flexShrink: 0, "& .MuiChip-label": { px: 0.75, fontSize: 12 } }}
              />
            </Stack>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              component="div"
              sx={{ minWidth: 0 }}
            >
              <span
                data-testid={`${PREFIX}-banner-deadline`}
                data-deadline={banner.effectiveDeadlineAt ?? ""}
              >
                {getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.bannerDeadline)}
                {": "}
                {banner.deadlineLabel}
              </span>
              {countdownText && (
                <>
                  {" · "}
                  <span
                    data-testid={`${PREFIX}-banner-countdown`}
                    data-working-seconds={banner.countdown.remainingSeconds ?? ""}
                    data-overdue-seconds={banner.countdown.overdueSeconds ?? ""}
                    data-clock-running={String(banner.countdown.running)}
                    aria-live="off"
                  >
                    {countdownText}
                  </span>
                </>
              )}
              {banner.countdown.paused && (
                <>
                  {" · "}
                  <span
                    data-testid={`${PREFIX}-banner-paused`}
                    title={
                      banner.countdown.nextClockChangeAt
                        ? `${getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.countdownResumes)}: ${banner.countdown.nextClockChangeAt}`
                        : undefined
                    }
                  >
                    {getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.countdownPaused)}
                  </span>
                </>
              )}
            </Typography>
            <SlaInstanceSummaryLine
              view={bannerProcess}
              target="process"
              testId={`${PREFIX}-banner-process`}
              noWrap
              sx={{ minWidth: 0 }}
            />
          </Box>
        </Alert>
      )}

      {/* Preferenze individuali: tre interruttori separati, per utente. */}
      <Stack
        data-testid={`${PREFIX}-preferences`}
        direction="row"
        alignItems="center"
        columnGap={2}
        flexWrap="wrap"
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle2">
            {getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.preferencesTitle)}
          </Typography>
          {/* Ambito `user:<id>@organization:<id>`: e' un identificativo tecnico,
              non una label per l'utente. Resta nel DOM (test e screen reader
              lo leggono) ma non a video. */}
          {preferencesScope && (
            <Typography
              variant="caption"
              data-testid={`${PREFIX}-preferences-scope`}
              sx={VISUALLY_HIDDEN}
            >
              {preferencesScope}
            </Typography>
          )}
        </Stack>
        {preferencesForbidden && (
          <Typography variant="caption" data-testid={`${PREFIX}-preferences-forbidden`}>
            {getTranslation(translations, SLA_ACTIVITIES_TEXT_KEYS.preferencesForbidden)}
          </Typography>
        )}
        <Stack direction="row" spacing={2} flexWrap="wrap">
          {SLA_ACTIVITY_PREFERENCE_KEYS.map((key) => (
            <FormControlLabel
              key={key}
              control={
                <Switch
                  size="small"
                  checked={preferences ? slaActivityPreferenceValue(preferences, key) : false}
                  disabled={!preferences}
                  onChange={(event) => void setPreference(key, event.target.checked)}
                  inputProps={{
                    "aria-label": getTranslation(
                      translations,
                      SLA_ACTIVITY_PREFERENCE_TEXT_KEYS[key],
                    ),
                    // `inputProps` tipizza gli attributi HTML standard: il
                    // `data-testid` dell'input reale (quello che porta lo stato
                    // checked) va passato senza perdere il tipo.
                    ...({ "data-testid": `${PREFIX}-pref-${key}` } as Record<string, string>),
                  }}
                />
              }
              label={getTranslation(
                translations,
                SLA_ACTIVITY_PREFERENCE_TEXT_KEYS[key],
              )}
            />
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default SlaActivitiesSurface;
